import Dropdown from '@components/drop-down/Dropdown';
import { months } from '@constants/date';

import Button from '../button/Button';
import {
  getBoundedMonthOptions,
  getBoundedYearOptions,
  getDefaultDateRange
} from './date-bounds';

import theme from '../../../themes/colors';

import './CalendarNavigator.css';

interface CalendarNavigatorProps {
  minDate?: Date;
  maxDate?: Date;
}

const primaryStroke = (theme as any).cofeePrimary6;

const BUTTON_BASE_CLASS =
  '!flex !size-7.5 !items-center !justify-center !rounded !bg-cofeeNeutralBg !p-1';

const DROPDOWN_SHARED_PROPS = {
  buttonClassName: '!rounded !bg-cofeeNeutralBg !border-0 !min-w-0 h-[30px]',
  labelStyle: '!text-sm !font-medium !text-content',
  isDividerNeeded: false,
  rightIconStyle: 'iconStrokePrimary6 !size-4',
  optionItemClassName: '!p-2',
  selectedOptionClassName: '!bg-white',
  hoverOptionClassName: 'hover:bg-cofeeNeutralBg',
  optionsStyle:
    'no-scrollbar !w-33 rounded-xl !border-cofeeNeutral4 px-2 !max-h-[350px] !overflow-y-auto'
};

const createNavigatorRenderer =
  (minDate: Date, maxDate: Date) =>
  (
    currFocusedDate: Date,
    changeShownDate: (
      value: string | number | Date,
      mode?: 'set' | 'setYear' | 'setMonth' | 'monthOffset'
    ) => void
  ) => {
    // Destructuring Date methods loses `this` binding
    /* eslint-disable react/destructuring-assignment */
    const monthIndex = currFocusedDate.getMonth();
    const currentYear = currFocusedDate.getFullYear();
    /* eslint-enable react/destructuring-assignment */

    const minYear = minDate.getFullYear();
    const maxYear = maxDate.getFullYear();
    const minMonth = minDate.getMonth();
    const maxMonth = maxDate.getMonth();

    const isMinYear = currentYear === minYear;
    const isMaxYear = currentYear === maxYear;

    const isPrevMonthDisabled = isMinYear && monthIndex === minMonth;
    const isNextMonthDisabled = isMaxYear && monthIndex === maxMonth;

    const monthOptions = getBoundedMonthOptions(currentYear, minDate, maxDate);
    const yearOptions = getBoundedYearOptions(minDate, maxDate);

    return (
      <div className="mt-4 flex h-7.5 items-center justify-between px-4">
        <Button
          handleClick={() => changeShownDate(-1, 'monthOffset')}
          variant="transparent"
          className={`${BUTTON_BASE_CLASS} ${
            isPrevMonthDisabled ? 'bg-cofeeGreyNeutral7 opacity-50' : ''
          }`}
          aria-label="Previous month"
          leftIconName="down-icon-dark"
          leftIconStroke={primaryStroke}
          leftIconStyle={`rotate-90 !mr-0 ${
            isPrevMonthDisabled
              ? 'iconStrokeGreyNeutral6'
              : 'iconStrokePrimary6'
          }`}
          disabled={isPrevMonthDisabled}
        />

        <div className="flex items-center gap-2">
          <Dropdown
            {...DROPDOWN_SHARED_PROPS}
            currentFilter={String(monthIndex)}
            filterOptions={monthOptions}
            onSelectFilter={(v) => changeShownDate(Number(v), 'setMonth')}
            placeholder={months[monthIndex].name}
            buttonClassName={`${DROPDOWN_SHARED_PROPS.buttonClassName} p-2`}
          />

          <Dropdown
            {...DROPDOWN_SHARED_PROPS}
            currentFilter={String(currentYear)}
            filterOptions={yearOptions}
            onSelectFilter={(v) => changeShownDate(Number(v), 'setYear')}
            placeholder={String(currentYear)}
            buttonClassName={`${DROPDOWN_SHARED_PROPS.buttonClassName} !px-2 !py-1`}
          />
        </div>

        <Button
          handleClick={() => changeShownDate(1, 'monthOffset')}
          variant="transparent"
          className={`${BUTTON_BASE_CLASS} ${
            isNextMonthDisabled ? 'bg-cofeeGreyNeutral7 opacity-50' : ''
          }`}
          aria-label="Next month"
          rightIconName="down-icon-dark"
          rightIconStroke={primaryStroke}
          rightIconStyle={`-rotate-90 !ml-0 ${
            isNextMonthDisabled
              ? 'iconStrokeGreyNeutral6'
              : 'iconStrokePrimary6'
          }`}
          disabled={isNextMonthDisabled}
        />
      </div>
    );
  };

const useCalendarNavigator = ({ minDate, maxDate }: CalendarNavigatorProps) => {
  const { minDate: defaultMin, maxDate: defaultMax } = getDefaultDateRange();
  return createNavigatorRenderer(minDate ?? defaultMin, maxDate ?? defaultMax);
};

export default useCalendarNavigator;
